import { useState, useRef, useEffect } from 'react';
import { useMediaDeviceSetup } from './recorder/useMediaDeviceSetup';
import { useAudioLevelMonitor } from './recorder/useAudioLevelMonitor';
import { useRecordingManager } from './recorder/useRecordingManager';
import type { AudioRecording } from './recorder/useRecordingManager';

export type { AudioRecording };

export const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<number | null>(null);
  
  // Use smaller hooks
  const { 
    errorMessage: deviceError, 
    streamRef, 
    analyserRef, 
    setupMediaDevices, 
    cleanupMediaDevices,
    setErrorMessage: setDeviceError 
  } = useMediaDeviceSetup();
  
  const { audioLevel } = useAudioLevelMonitor({ 
    analyser: analyserRef.current, 
    isMonitoring: isRecording 
  });
  
  const {
    recordings,
    errorMessage: recordingError,
    mediaRecorderRef,
    initializeRecorder,
    startRecording: startMediaRecording,
    stopRecording: stopMediaRecording,
    clearRecordings,
    deleteRecording,
    downloadRecording,
    setErrorMessage: setRecordingError
  } = useRecordingManager();
  
  // Combine error messages from both sources
  const errorMessage = deviceError || recordingError;
  
  // Start recording
  const startRecording = async () => {
    try {
      // Set up media devices if not already set up
      const devicesReady = await setupMediaDevices();
      if (!devicesReady || !streamRef.current) {
        return;
      }
      
      // Initialize the recorder
      const recorderReady = initializeRecorder(streamRef.current);
      if (!recorderReady) {
        return;
      }
      
      // Start recording
      const recordingStarted = startMediaRecording();
      if (!recordingStarted) {
        return;
      }
      
      // Start the timer for elapsed time
      setElapsedTime(0);
      timerRef.current = window.setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      
      setIsRecording(true);
      console.log("Recording started successfully");
    } catch (err) {
      console.error("Error starting recording:", err);
      setDeviceError(`Could not start recording: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    // Stop the media recorder
    stopMediaRecording();
    
    // Clear the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsRecording(false);
    console.log("Recording stopped");
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop recording if active
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          console.error("Error stopping media recorder:", e);
        }
      }
      
      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Clean up media devices
      cleanupMediaDevices();
      
      // Clean up any object URLs to prevent memory leaks
      recordings.forEach(recording => {
        URL.revokeObjectURL(recording.url);
      });
    };
  }, [recordings]);
  
  return {
    isRecording,
    recordings,
    elapsedTime,
    audioLevel,
    errorMessage,
    startRecording,
    stopRecording,
    downloadRecording,
    clearRecordings,
    deleteRecording
  };
}
